import * as classnames from "classnames";
import * as React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { generate as generateKey } from "shortid";

import {
  ApplicantChallengeTitleColumn,
  Icon,
  Msg,
} from "../../../../../shared/components";
import { ApplicantExamModel } from "../../../../../shared/models";
import { ChallengeStyle } from "../../../../../shared/services/enums";

/**
 * Prop interface
 */
export interface EditorSideMenuProps {
  tutorial?: boolean;
  exam: ApplicantExamModel;
}

/**
 * State interface
 */
export interface EditorSideMenuState {
  isOpen: boolean;
}

/**
 * Page component
 */
const EditorSideMenu = ({ tutorial, exam }: EditorSideMenuProps) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const location = useLocation();
  const hasHParameter = location.search.includes("h=1");

  const onClickOpenMenu = (e: React.SyntheticEvent<EventTarget>) => {
    e.stopPropagation();
    setIsOpen((open) => !open);
  };

  const onClickMenuItem = () => {
    setIsOpen(false);
  };

  const rootStyle = classnames("code-editor-side-menu", "menu", {
    "is-open": isOpen,
  });

  const orgname = tutorial ? "tutorial" : exam.org.name;

  const challengeItems = exam.challenges.map((challenge, index: number) => {
    let statusBadgeMini = null;
    if (exam.isChallengeStarted(challenge.id)) {
      statusBadgeMini = (
        <span className="code-editor-side-menu__list-item-icon__badge is-started">
          <Icon type="play" size="small" />
        </span>
      );
    } else if (exam.isChallengeFinished(challenge.id)) {
      statusBadgeMini = (
        <span className="code-editor-side-menu__list-item-icon__badge is-finished">
          <Icon type="check" size="small" />
        </span>
      );
    }

    const itemStyle = classnames(
      "code-editor-side-menu__list-item",
      "is-steps",
      {
        "is-top-steps": index === 0,
      },
    );

    const route = `/${orgname}/exams/${exam.urlToken}/challenges/${
      challenge.id
    }${challenge.style === ChallengeStyle.Quiz ? "/1" : ""}${
      ChallengeStyle.shouldUseVsCodeEditor(challenge.style) ? "?h=1" : ""
    }`;

    return (
      <li key={challenge.id} className={itemStyle}>
        <NavLink
          className="code-editor-side-menu__list-item-link"
          to={route}
          onClick={
            hasHParameter &&
            ChallengeStyle.shouldUseVsCodeEditor(challenge.style)
              ? undefined
              : (e) => {
                  e.preventDefault();
                  window.location.href = route;
                }
          }
        >
          <div className="code-editor-side-menu__list-item-icon">
            <Icon type="circle-thin" size="large" />
            <span className="code-editor-side-menu__list-item-icon__step">
              {index + 1}
            </span>
            {statusBadgeMini}
          </div>
          <div className="code-editor-side-menu__list-item-text">
            <ApplicantChallengeTitleColumn challenge={challenge} exam={exam} />
          </div>
        </NavLink>
      </li>
    );
  });

  challengeItems.push(
    <li
      className="code-editor-side-menu__list-item is-steps is-bottom-steps"
      key={generateKey()}
    >
      <NavLink
        className="code-editor-side-menu__list-item-link"
        to={`/${orgname}/exams/${exam.urlToken}/submit`}
        exact={true}
      >
        <div className="code-editor-side-menu__list-item-icon">
          <Icon type="flag-checkered" size="large" />
        </div>
        <div className="code-editor-side-menu__list-item-text">
          <Msg id="editor-side-menu.submitExam" defaultMessage="試験の提出" />
        </div>
      </NavLink>
    </li>,
  );

  const challengeList = exam.isStarted() && (
    <li className="code-editor-side-menu__list-item">
      <NavLink
        className="code-editor-side-menu__list-item-link"
        to={`/${orgname}/exams/${exam.urlToken}/challenges`}
        exact={true}
      >
        <div className="code-editor-side-menu__list-item-icon">
          <Icon type="table" size="large" />
        </div>
        <div className="code-editor-side-menu__list-item-text">
          <Msg id="editor-side-menu.challengeList" defaultMessage="問題一覧" />
        </div>
      </NavLink>
    </li>
  );

  return (
    <aside className={rootStyle} onClick={onClickMenuItem}>
      <ul
        className="code-editor-side-menu__list"
        role="navigation"
        aria-label="Applicant Side Menu"
      >
        <div className="code-editor-side-menu__dummy-background" />
        <li className="code-editor-side-menu__list-item">
          <a
            className="code-editor-side-menu__list-item-link"
            onClick={onClickOpenMenu}
          >
            <div className="code-editor-side-menu__list-item-icon">
              <div className="icon-bar">
                <div className="icon-bar-line" />
                <div className="icon-bar-line" />
                <div className="icon-bar-line" />
              </div>
            </div>
            <div className="code-editor-side-menu__list-item-text has-close">
              <div className="icon-cross">
                <div className="icon-bar-line" />
                <div className="icon-bar-line" />
              </div>
            </div>
          </a>
        </li>
        <li className="code-editor-side-menu__list-item">
          <NavLink
            className="code-editor-side-menu__list-item-link"
            to={`/${orgname}/exams/${exam.urlToken}`}
            exact={true}
          >
            <div className="code-editor-side-menu__list-item-icon">
              <Icon type="info-circle" size="large" />
            </div>
            <div className="code-editor-side-menu__list-item-text">
              <Msg id="editor-side-menu.outline" defaultMessage="概要" />
            </div>
          </NavLink>
        </li>
        {challengeList}
        {exam.isStarted() && challengeItems}
      </ul>
    </aside>
  );
};

export default EditorSideMenu;
